import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";

import usePopover from "components/common/usePopover";
import EventService from "service/event/EventService";
import PatientService from "../service/PatientService";
import "../styles/reschedule.scss";

/**
 * "Update missing profile details" — the step bridge runs BEFORE its reschedule
 * dialog opens (navigo-module DocumentUpload.js checkPatientInfo /
 * updatePatientInfo and the dialog at ~5190). Opened by useRescheduleFlow when
 * the member's record has no date of birth or no email.
 *
 * Bridge's copy and rules, in phr's look: "User name" (read-only, printed as
 * `${firstName} ${lastName}` exactly as bridge does), "Date of birth *" only
 * when it is missing (DD MMM YYYY, 100 years back to today), "Email Id" only
 * when it is missing (50 characters, spaces dropped as typed), and one "Save".
 * No cancel button — the backdrop (and Escape) closes it while not saving.
 *
 * Save: "Please fill the Date of birth" / "Please enter a valid email address",
 * then PUT /v1/patients/profile/update (a full-record replace, so the body is
 * bridge's, field for field) and "Information updated" — after which the
 * reschedule dialog opens. With the date of birth already there and the email
 * left blank, nothing is sent and it goes straight on, as bridge does.
 *
 * One deliberate difference: a typed date that is not a valid "DD MMM YYYY"
 * inside the allowed range counts as NOT filled. Bridge's KeyboardDatePicker
 * would PUT it anyway ("Invalid date", or a birth date in the future).
 */

const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTH_FULL = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const pad = (n) => String(n).padStart(2, "0");
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const dmy = (d) => `${pad(d.getDate())} ${MON[d.getMonth()]} ${d.getFullYear()}`;
const startOfToday = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

/** Bridge's isDOBEmpty — note a non-numeric string also counts as empty. */
export const isDOBEmpty = (patient) => {
  const dob = patient && patient.dateOfBirth;
  return dob === undefined || dob === null || dob === "" || isNaN(dob) || Number(dob) === 0;
};

/** Bridge's isEmailEmpty. */
export const isEmailEmpty = (patient) => {
  const email = patient && patient.email;
  return email === undefined || email === null || typeof email !== "string" || email.trim() === "";
};

/** Bridge's isValidEmail, verbatim. */
const isValidEmail = (email) => {
  if (typeof email !== "string" || email.trim() === "" || !email.includes("@")) return false;
  const [localPart, domain] = email.trim().split("@");
  if (!localPart || !domain) return false;
  return /^[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}$/.test(domain);
};

/** "05 Mar 1990" → Date, or null. */
const parseDmy = (text) => {
  const m = /^(\d{1,2}) ([A-Za-z]{3}) (\d{4})$/.exec(String(text).trim());
  if (!m) return null;
  const mi = MON.findIndex((x) => x.toLowerCase() === m[2].toLowerCase());
  if (mi < 0) return null;
  const d = new Date(Number(m[3]), mi, Number(m[1]));
  return d.getMonth() === mi && d.getDate() === Number(m[1]) ? d : null;
};

/**
 * The date-of-birth field: typed as "DD MMM YYYY" (bridge's format and
 * placeholder), with a calendar beside it. Shows the MUI picker's messages
 * under the field when the text is not a usable date.
 */
const DobField = ({ min, max, value, text, onText, onPick }) => {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const { triggerRef, menuRef, menuStyle } = usePopover(open, close);
  const base = value || max;
  const [view, setView] = useState(() => new Date(base.getFullYear(), base.getMonth(), 1));
  useEffect(() => {
    if (open) setView(new Date(base.getFullYear(), base.getMonth(), 1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const grid = useMemo(() => {
    const lead = new Date(view.getFullYear(), view.getMonth(), 1).getDay();
    const days = new Date(view.getFullYear(), view.getMonth() + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < lead; i += 1) cells.push(null);
    for (let d = 1; d <= days; d += 1) cells.push(new Date(view.getFullYear(), view.getMonth(), d));
    return cells;
  }, [view]);
  const years = useMemo(() => {
    const out = [];
    for (let y = max.getFullYear(); y >= min.getFullYear(); y -= 1) out.push(y);
    return out;
  }, [min, max]);

  const parsed = text ? parseDmy(text) : null;
  const helper = !text
    ? ""
    : !parsed
    ? "Invalid Date Format"
    : parsed < min
    ? "Date should not be before minimal date"
    : parsed > max
    ? "Date should not be after maximal date"
    : "";

  return (
    <>
      <div ref={triggerRef} className={`rs-dob${helper ? " is-invalid" : ""}`}>
        <input
          id="pd-dob"
          type="text"
          className="rs-input"
          placeholder="DD MMM YYYY"
          autoComplete="off"
          value={text}
          onChange={(e) => onText(e.target.value)}
        />
        <button
          type="button"
          className="rs-dob-cal"
          aria-label="Open calendar"
          onClick={() => setOpen((o) => !o)}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"
            strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="3.5" y="5" width="17" height="15.5" rx="2.5" />
            <path d="M3.5 9.5h17M8 3v4M16 3v4" />
          </svg>
        </button>
      </div>
      {helper ? <span className="rs-input-error">{helper}</span> : null}
      {open &&
        ReactDOM.createPortal(
          <div ref={menuRef} className="rs-cal" style={{ top: menuStyle.top, left: menuStyle.left }} role="dialog" aria-label="Pick a date of birth">
            <div className="rs-cal-head">
              <select
                className="rs-cal-select"
                aria-label="Month"
                value={view.getMonth()}
                onChange={(e) => setView(new Date(view.getFullYear(), Number(e.target.value), 1))}
              >
                {MONTH_FULL.map((m, i) => (
                  <option key={m} value={i}>{m}</option>
                ))}
              </select>
              <select
                className="rs-cal-select"
                aria-label="Year"
                value={view.getFullYear()}
                onChange={(e) => setView(new Date(Number(e.target.value), view.getMonth(), 1))}
              >
                {years.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
            <div className="rs-cal-grid">
              {DOW.map((d) => (
                <span className="rs-cal-dow" key={d}>{d}</span>
              ))}
              {grid.map((d, i) =>
                d ? (
                  <button
                    key={ymd(d)}
                    type="button"
                    className={`rs-cal-day${value && ymd(value) === ymd(d) ? " is-selected" : ""}`}
                    disabled={d < min || d > max}
                    onClick={() => {
                      setOpen(false);
                      onPick(d);
                    }}
                  >
                    {d.getDate()}
                  </button>
                ) : (
                  <span key={`pad-${i}`} className="rs-cal-pad" />
                )
              )}
            </div>
          </div>,
          document.body
        )}
    </>
  );
};
DobField.propTypes = {
  min: PropTypes.instanceOf(Date).isRequired,
  max: PropTypes.instanceOf(Date).isRequired,
  value: PropTypes.instanceOf(Date),
  text: PropTypes.string.isRequired,
  onText: PropTypes.func.isRequired,
  onPick: PropTypes.func.isRequired,
};

const ProfileDetailsDialog = ({ patient, onSaved, onClose }) => {
  const service = useRef(new PatientService()).current;
  const mounted = useRef(true);
  const panelRef = useRef(null);
  const [dobText, setDobText] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [saving, setSaving] = useState(false);

  const max = useMemo(() => startOfToday(), []);
  const min = useMemo(() => {
    const t = startOfToday();
    return new Date(t.getFullYear() - 100, t.getMonth(), t.getDate());
  }, []);
  const parsedDob = parseDmy(dobText);
  /* newDob: a usable date, or null (see the header on the one difference). */
  const newDob = parsedDob && parsedDob >= min && parsedDob <= max ? parsedDob : null;

  useEffect(() => {
    mounted.current = true;
    const last = document.activeElement;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    if (panelRef.current) panelRef.current.focus();
    return () => {
      mounted.current = false;
      document.body.style.overflow = prevOverflow;
      if (last && last.focus) last.focus();
    };
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !saving) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [saving, onClose]);

  const save = () => {
    if (saving) return;
    if (isDOBEmpty(patient) && newDob === null) {
      EventService.emit("showError", { message: "Please fill the Date of birth" });
      return;
    }
    if (isEmailEmpty(patient) && newEmail !== "" && isValidEmail(newEmail) === false) {
      EventService.emit("showError", { message: "Please enter a valid email address" });
      return;
    }
    const body = {
      patientId: patient.id,
      firstName: patient.firstName,
      lastName: patient.lastName ? patient.lastName : null,
      gender: patient.gender,
      dateOfBirth: isDOBEmpty(patient) ? ymd(newDob) : ymd(new Date(Number(patient.dateOfBirth))),
      phoneNo: String(patient.phoneno),
      email: newEmail !== "" ? newEmail : patient.email,
      profileImage: patient.profileImage,
      countryCode: patient.countryCode,
    };
    const done = () => {
      EventService.emit("showError", { message: "Information updated", tone: "success" });
      if (!mounted.current) return;
      setSaving(false);
      onSaved();
    };
    if (!isDOBEmpty(patient) && newEmail === "") {
      done();
      return;
    }
    setSaving(true);
    service.updatePatientProfile(body, done, (err) => {
      EventService.emit("showError", {
        message: `Error: ${err === null || err === undefined ? undefined : err.message}`,
      });
      if (mounted.current) setSaving(false);
    });
  };

  return ReactDOM.createPortal(
    <div
      className="rs-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !saving) onClose();
      }}
    >
      <div
        className="rs-modal rs-profile"
        role="dialog"
        aria-modal="true"
        aria-label="Update missing profile details"
        ref={panelRef}
        tabIndex={-1}
      >
        <div className="rs-head">
          <h2 className="rs-title">Update missing profile details</h2>
          <button type="button" className="rs-btn rs-btn-primary rs-profile-save" disabled={saving} onClick={save}>
            Save
          </button>
        </div>
        <div className="rs-body">
          <div className="rs-field">
            <label className="rs-label" htmlFor="pd-name">User name</label>
            <input
              id="pd-name"
              type="text"
              className="rs-input"
              value={`${patient.firstName} ${patient.lastName}`}
              disabled
            />
          </div>
          {isDOBEmpty(patient) && (
            <div className="rs-field">
              <label className="rs-label" htmlFor="pd-dob">
                Date of birth<span className="rs-req">*</span>
              </label>
              <DobField
                min={min}
                max={max}
                value={newDob}
                text={dobText}
                onText={setDobText}
                onPick={(d) => setDobText(dmy(d))}
              />
            </div>
          )}
          {isEmailEmpty(patient) && (
            <div className="rs-field">
              <label className="rs-label" htmlFor="pd-email">Email Id</label>
              <input
                id="pd-email"
                type="text"
                className="rs-input"
                value={newEmail}
                onChange={(e) => {
                  if (e.target.value.length <= 50) setNewEmail(e.target.value.trim());
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

ProfileDetailsDialog.propTypes = {
  /** The member's freshly fetched patient record. */
  patient: PropTypes.object.isRequired,
  /** Called after "Information updated" — the reschedule dialog opens next. */
  onSaved: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default ProfileDetailsDialog;
